import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { 
    ArrowLeft, ArrowRight, Loader2, Check, Sparkles, Info,
    Layers, Eye, Brain
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXIGENCIAS_INTERNAS = [
    { id: 'perfeccion', label: 'Perfección', description: 'Sentir que debe salir perfecto' },
    { id: 'aprobacion', label: 'Aprobación externa', description: 'Necesidad de que otros validen el resultado' },
    { id: 'competencia', label: 'Demostrar competencia', description: 'Probar que soy capaz' },
    { id: 'control', label: 'Control total', description: 'Necesidad de controlar cada detalle' },
    { id: 'novedad', label: 'Ser original', description: 'Hacer algo único o diferente' },
    { id: 'rapidez', label: 'Hacerlo rápido', description: 'Presión por terminar pronto' },
    { id: 'evitar_error', label: 'No equivocarme', description: 'Miedo a cometer errores' },
    { id: 'satisfacer', label: 'Satisfacer a otros', description: 'Cumplir expectativas ajenas' },
    { id: 'autonomia', label: 'Hacerlo solo/a', description: 'No pedir ayuda' },
    { id: 'significado', label: 'Que tenga sentido', description: 'Encontrar propósito en la tarea' }
];

export default function Modulo4DobleCapa() {
    const navigate = useNavigate();
    const [tareaVisible, setTareaVisible] = useState('');
    const [exigenciasSeleccionadas, setExigenciasSeleccionadas] = useState([]);
    const [insight, setInsight] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');

    // Load draft on mount
    useEffect(() => {
        const loadDraft = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/doble_capa`, {
                    withCredentials: true
                });
                if (data.data) {
                    if (data.data.tareaVisible) setTareaVisible(data.data.tareaVisible);
                    if (data.data.exigenciasSeleccionadas) setExigenciasSeleccionadas(data.data.exigenciasSeleccionadas);
                    if (data.data.insight) setInsight(data.data.insight);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch (error) {
                // silent - draft not found is expected
            } finally {
                setLoadingDraft(false);
            }
        };
        loadDraft();
    }, []);

    // Auto-save draft
    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(
                `${API_URL}/api/user/draft/save`,
                { 
                    module_id: 'doble_capa', 
                    data: { tareaVisible, exigenciasSeleccionadas, insight } 
                },
                { withCredentials: true }
            );
        } catch (error) {
            // silent - non-critical draft save
        } finally {
            setSavingDraft(false);
        }
    }, [tareaVisible, exigenciasSeleccionadas, insight]);

    useEffect(() => {
        if (loadingDraft || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [tareaVisible, exigenciasSeleccionadas, insight, saveDraft, loadingDraft, readOnly]);

    const toggleExigencia = (id) => {
        setExigenciasSeleccionadas(prev => 
            prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
        );
    };

    const canSubmit = !readOnly && tareaVisible.trim().length > 0 && 
                      exigenciasSeleccionadas.length >= 1 && 
                      insight.trim().length > 0;

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) {
            toast.error('Completa todos los campos requeridos');
            return;
        }

        setLoading(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/user/modules/doble-capa/submit`,
                { 
                    tarea_visible: tareaVisible,
                    exigencias_internas: exigenciasSeleccionadas,
                    insight
                },
                { withCredentials: true }
            );
            setAiInsight(data.ai_insight);
            setSubmitted(true);
            toast.success('Módulo completado exitosamente');
        } catch (error) {
            const message = error.response?.data?.detail || 'Error al enviar el módulo';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (loadingDraft) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" />
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-lg animate-fade-in-up">
                    <CardContent className="pt-8 text-center">
                        <div className="w-20 h-20 bg-[#5FAF9D] rounded-full flex items-center justify-center mx-auto mb-6">
                            <Layers className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-4">¡Doble capa completada!</h2>
                        
                        {/* Result summary */}
                        <div className="text-left mb-6 p-4 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                            <div className="flex items-center gap-2 mb-3">
                                <Eye className="h-4 w-4 text-[#3E5F6E]" />
                                <span className="text-xs font-semibold text-[#3E5F6E] uppercase">Tarea visible</span>
                            </div>
                            <p className="text-[#1F2A30] mb-4">{tareaVisible}</p>
                            
                            <div className="flex items-center gap-2 mb-3">
                                <Brain className="h-4 w-4 text-[#5FAF9D]" />
                                <span className="text-xs font-semibold text-[#5FAF9D] uppercase">Exigencias internas</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {exigenciasSeleccionadas.map(id => {
                                    const exigencia = EXIGENCIAS_INTERNAS.find(e => e.id === id);
                                    return (
                                        <span key={id} className="px-3 py-1 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm">
                                            {exigencia?.label}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                        
                        {aiInsight && (
                            <div className="senda-ai-panel text-left mb-6">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-2">Reflexión guiada</p>
                                        <p className="text-[#1F2A30] italic">"{aiInsight}"</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <p className="text-[#4A5B63] mb-6">
                            Has identificado la capa oculta detrás de tu tarea. Reconocer estas exigencias internas es el primer paso para manejarlas.
                        </p>
                        
                        <div className="mb-6"><ModuleFeedback moduleId="doble_capa" chapter={1} /></div>
                        <Link to="/usuario">
                            <Button className="senda-btn-primary" data-testid="back-to-dashboard-doble-capa">
                                Continuar
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario">
                                <Button variant="ghost" size="icon" className="text-[#3E5F6E]" data-testid="back-to-usuario-doble-capa">
                                    <ArrowLeft className="h-5 w-5" />
                                </Button>
                            </Link>
                            <div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']">Doble capa de tarea</h1>
                                <p className="text-sm text-[#4A5B63]">Identifica la demanda oculta</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {savingDraft && !readOnly && (
                                <span className="text-xs text-[#7A8A93] flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Guardando...
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="senda-module-container animate-fade-in-up">
                    <CardHeader className="pb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-[#3E5F6E] rounded-xl flex items-center justify-center">
                                <Layers className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-[#3E5F6E]">La tarea y su capa oculta</CardTitle>
                                <p className="text-sm text-[#4A5B63]">Descubre qué hay detrás de lo que evitas</p>
                            </div>
                        </div>
                        
                        <div className="p-4 bg-[#F5F7F8] rounded-lg border border-[#DCE3E8] flex items-start gap-3">
                            <Info className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" />
                            <p className="text-sm text-[#4A5B63]">
                                Muchas veces, no es la tarea en sí lo que evitamos, sino una exigencia oculta detrás de ella. 
                                Identificarla te permite abordarla de forma más consciente.
                            </p>
                        </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-8">
                        {/* Tarea Visible */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Eye className="h-5 w-5 text-[#3E5F6E]" />
                                <Label className="senda-label mb-0">¿Qué tarea estás evitando o postergando?</Label>
                            </div>
                            <Input
                                value={tareaVisible}
                                onChange={(e) => setTareaVisible(e.target.value)}
                                placeholder="Ej: Escribir el informe trimestral, llamar al cliente, estudiar para el examen..."
                                className="senda-input"
                                data-testid="tarea-visible-input"
                            />
                            <p className="text-xs text-[#7A8A93]">Describe la tarea de forma específica</p>
                        </div>

                        {/* Exigencias Internas */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <Brain className="h-5 w-5 text-[#5FAF9D]" />
                                <Label className="senda-label mb-0">¿Qué exigencias internas hay detrás? (selecciona una o más)</Label>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {EXIGENCIAS_INTERNAS.map((exigencia) => (
                                    <button
                                        key={exigencia.id}
                                        onClick={() => toggleExigencia(exigencia.id)}
                                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                                            exigenciasSeleccionadas.includes(exigencia.id)
                                                ? 'border-[#5FAF9D] bg-[#5FAF9D]/5'
                                                : 'border-[#DCE3E8] hover:border-[#3E5F6E]/50'
                                        }`}
                                        data-testid={`exigencia-${exigencia.id}`}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                                exigenciasSeleccionadas.includes(exigencia.id)
                                                    ? 'border-[#5FAF9D] bg-[#5FAF9D]'
                                                    : 'border-[#DCE3E8]'
                                            }`}>
                                                {exigenciasSeleccionadas.includes(exigencia.id) && (
                                                    <Check className="h-3 w-3 text-white" />
                                                )}
                                            </div>
                                            <span className={`font-medium ${
                                                exigenciasSeleccionadas.includes(exigencia.id)
                                                    ? 'text-[#3E5F6E]'
                                                    : 'text-[#4A5B63]'
                                            }`}>
                                                {exigencia.label}
                                            </span>
                                        </div>
                                        <p className="text-xs text-[#7A8A93] ml-7">{exigencia.description}</p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Insight */}
                        <div className="space-y-2">
                            <Label className="senda-label">¿Qué descubres al ver esta conexión?</Label>
                            <Textarea
                                value={insight}
                                onChange={(e) => setInsight(e.target.value)}
                                placeholder="Reflexiona sobre cómo esta exigencia oculta puede estar influyendo en tu evitación. ¿Qué te dice esto sobre ti mismo/a?"
                                className="senda-input min-h-[120px]"
                                data-testid="insight-doble-capa"
                            />
                            <p className="text-xs text-[#7A8A93]">Sé honesto/a contigo mismo/a.</p>
                        </div>

                        {/* Submit */}
                        <div className="flex justify-end pt-6 border-t border-[#DCE3E8]">
                            <Button
                                onClick={handleSubmit}
                                disabled={!canSubmit || loading}
                                className="senda-btn-primary"
                                data-testid="submit-doble-capa-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        Completar módulo
                                        <Check className="h-4 w-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
